import { useCallback, useEffect, useRef, useState } from 'react';
import { Card, EmptyState, Progress, PageHeader } from '@/components/ui';
import { Icon } from '@/components/ui/Icon';
import { agentApi } from '@/shared/api/agentApi';
import { useSipStore } from '../stores/sipStore';
import { toast } from '@/components/ui/Toast';

interface Campaign { id: number; name: string; dialer_mode: string }
interface ProgressData { total: number; remaining: number; done: number }

/**
 * Predictive-mode agent screen. Unlike the Preview dialer, the agent does NOT
 * dial leads one at a time here — the SERVER dials several lines per agent
 * automatically (PredictiveDialer::tick) and bridges the first customer who
 * answers straight into the agent's softphone as an inbound call. So this
 * screen's whole job is to mark the agent "available" (presence) and wait;
 * the global softphone in the layout picks up the bridged call.
 */
export function PredictiveStandby({ campaign, onExit }: { campaign: Campaign; onExit: () => void }) {
  const [onShift, setOnShift] = useState(false);
  const [progress, setProgress] = useState<ProgressData | null>(null);
  const sipState = useSipStore((s) => s.state);
  const registered = useSipStore((s) => s.registered);

  // Don't tear the agent off a live bridged call when they pause/leave.
  const onLiveCall = sipState === 'ringing-in' || sipState === 'on-call' || sipState === 'on-hold';
  const presenceTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadProgress = useCallback(() => {
    void agentApi.dialerProgress(campaign.id).then((p) => setProgress(p as ProgressData)).catch(() => {});
  }, [campaign.id]);

  useEffect(() => { loadProgress(); }, [loadProgress]);

  // While on shift, refresh progress + re-assert "available" presence on a
  // heartbeat so a stale/expired presence row doesn't quietly drop the agent
  // out of the predictive pool mid-shift.
  useEffect(() => {
    if (!onShift) return;
    presenceTimer.current = setInterval(() => {
      loadProgress();
      // Re-assert availability ONLY when idle — never override an active call.
      if (!onLiveCall) void agentApi.setPresence('available').catch(() => {});
    }, 15000);
    return () => { if (presenceTimer.current) clearInterval(presenceTimer.current); };
  }, [onShift, onLiveCall, loadProgress]);

  async function startShift() {
    // הרישום מנוהל גלובלית (useSipRegistration); register() בלי קרדנצ'לס היה
    // no-op שדורס את הרישום האמיתי. בלי קו — אין משמרת.
    if (!registered) {
      toast('הסופטפון לא מחובר — המתן לחיבור או רענן את הדף', 'danger');
      return;
    }
    try {
      await agentApi.setPresence('available');
      setOnShift(true);
      toast('אתה זמין — השרת מתחיל לחייג עבורך', 'ok');
    } catch {
      toast('שגיאה בכניסה למשמרת', 'danger');
    }
  }

  async function endShift() {
    try {
      await agentApi.setPresence('offline');
    } catch { /* best-effort */ }
    setOnShift(false);
    toast('יצאת ממצב חיוג', 'ok');
  }

  // עזיבת המסך משחררת מהמאגר החזוי — אבל רק אם באמת נכנסנו למשמרת,
  // ולעולם לא באמצע שיחה חיה (זה היה מפיל presence על נציג בשיחה).
  const shiftRef = useRef({ onShift: false, onLiveCall: false });
  shiftRef.current = { onShift, onLiveCall };
  useEffect(() => {
    return () => {
      if (shiftRef.current.onShift && !shiftRef.current.onLiveCall) {
        void agentApi.setPresence('offline').catch(() => {});
      }
    };
  }, []);

  return (
    <div className="stack" style={{ padding: 'var(--gap)', maxWidth: 560 }}>
      <PageHeader
        title={campaign.name}
        actions={
          <button
            type="button"
            className="btn ghost"
            disabled={onLiveCall}
            title={onLiveCall ? 'אי אפשר לצאת באמצע שיחה חיה' : undefined}
            onClick={() => { if (onShift) void endShift(); onExit(); }}
          >
            ← חזרה לקמפיינים
          </button>
        }
      />

      {progress && (
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--t-sm)', marginBottom: 6 }}>
            <span>טופלו {progress.done} מתוך {progress.total}</span>
            <span style={{ color: 'var(--muted)' }}>נותרו {progress.remaining}</span>
          </div>
          <Progress value={progress.done} max={progress.total || 1} />
        </Card>
      )}

      <Card>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <Icon name="target" size={24} color="var(--accent)" />
          <div style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)', lineHeight: 1.6 }}>
            <strong style={{ color: 'var(--text)' }}>חיוג חזוי (אוטומטי)</strong>
            <div>במצב זה <b>המערכת מחייגת עבורך</b> כמה לקוחות במקביל. אינך מחייג ידנית.
            הלקוח הראשון שעונה יחובר אליך אוטומטית כשיחה נכנסת, והשאר חוזרים לתור.</div>
          </div>
        </div>
      </Card>

      {!onShift ? (
        <Card>
          <div className="stack">
            <EmptyState
              icon="phone"
              title="מוכן להתחיל משמרת חיוג?"
              description="בלחיצה תסומן כזמין והמערכת תתחיל לחייג ולחבר אליך שיחות."
            />
            <button type="button" className="btn primary" onClick={startShift}>
              <Icon name="play" size={14} /> התחל משמרת
            </button>
          </div>
        </Card>
      ) : (
        <Card>
          <div className="stack" style={{ alignItems: 'center', textAlign: 'center', padding: '12px 0' }}>
            {onLiveCall ? (
              <>
                <Icon name="phone" size={32} color="var(--ok, #16a34a)" />
                <strong>שיחה פעילה</strong>
                <div style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>הלקוח חובר אליך. כרטיס הלקוח נפתח אוטומטית.</div>
              </>
            ) : (
              <>
                <span className="pulse-dot" style={{
                  width: 14, height: 14, borderRadius: '50%',
                  background: 'var(--ok, #16a34a)', display: 'inline-block',
                  boxShadow: '0 0 0 0 rgba(22,163,74,0.5)', animation: 'pulse 1.6s infinite',
                }} />
                <strong>ממתין לשיחה…</strong>
                <div style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>
                  המערכת מחייגת ברקע. שיחה תיכנס ברגע שלקוח יענה.
                </div>
                <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
                  סטטוס חיבור: {registered ? 'מחובר' : 'מתחבר…'}
                </div>
              </>
            )}
            <button type="button" className="btn" onClick={endShift} disabled={onLiveCall}>
              עצור משמרת
            </button>
          </div>
        </Card>
      )}

      <style>{`@keyframes pulse {
        0% { box-shadow: 0 0 0 0 rgba(22,163,74,0.5); }
        70% { box-shadow: 0 0 0 12px rgba(22,163,74,0); }
        100% { box-shadow: 0 0 0 0 rgba(22,163,74,0); }
      }`}</style>
    </div>
  );
}
